import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { BookOpen, ClipboardList, Calendar, Loader2, X, Plus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LMS() {
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("courses");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: "", course_code: "", course_name: "", description: "", due_date: "", max_marks: 100 });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [c, a] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/courses/assignments`, { withCredentials: true }),
      ]);
      setCourses(c.data); setAssignments(a.data);
    } catch {} finally { setLoading(false); }
  };

  const handleAddAssignment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/courses/assignments`, form, { withCredentials: true });
      setShowModal(false);
      fetchAll();
    } catch {}
  };

  const ACCENT = ["#0A7AFF", "#8B5CF6", "#22C55E", "#F59E0B", "#FF3636", "#06B6D4"];

  return (
    <div className="space-y-6" data-testid="lms-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Learning Management System</h2>
        </div>
        {activeTab === "assignments" && (
          <button onClick={() => setShowModal(true)} data-testid="add-assignment-btn"
            className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> New Assignment
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#D8D4CB]">
        {[["courses", "Courses", BookOpen], ["assignments", "Assignments", ClipboardList]].map(([t, label, Icon]) => (
          <button key={t} onClick={() => setActiveTab(t)} data-testid={`lms-tab-${t}`}
            className={`flex items-center gap-2 font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-5 py-3 border-b-2 transition-all ${activeTab === t ? "border-[#0C0C10] text-[#0C0C10]" : "border-transparent text-[#BDBAA8]"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {activeTab === "courses" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? <div className="col-span-3 flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
            : courses.map((c, i) => (
            <div key={c._id} className="bg-white border border-[#E2DDD4] p-6 hover:border-[#0C0C10] transition-colors group"
              style={{ borderLeft: `3px solid ${ACCENT[i % 6]}` }}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-['JetBrains_Mono'] text-[10px] text-[#BDBAA8] mb-1" style={{ color: ACCENT[i % 6] }}>{c.code}</p>
                  <h3 className="font-['Syne'] font-bold text-sm text-[#0C0C10] leading-tight">{c.name}</h3>
                </div>
                <span className="font-['Syne'] text-[9px] font-bold uppercase tracking-wider text-white px-2 py-1" style={{ background: ACCENT[i % 6] }}>
                  Sem {c.semester}
                </span>
              </div>
              <p className="font-['DM_Sans'] text-xs text-[#7A7468] mb-4">{c.faculty_name} — {c.department}</p>
              <div className="flex items-center justify-between text-[10px]">
                <span className="font-['Syne'] text-[#BDBAA8] uppercase tracking-wider">{c.total_students} students</span>
                <span className="font-['Syne'] text-[#BDBAA8] uppercase tracking-wider">{c.credits} credits</span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between mb-1">
                  <span className="font-['Syne'] text-[9px] uppercase tracking-wider text-[#BDBAA8]">Attendance</span>
                  <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{c.avg_attendance}%</span>
                </div>
                <div className="w-full h-1 bg-[#F0ECE4]">
                  <div className="h-1 transition-all duration-500" style={{ width: `${c.avg_attendance}%`, background: ACCENT[i % 6] }} />
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {activeTab === "assignments" && (
        <div className="bg-white border border-[#E2DDD4]">
          <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "2fr 1fr 80px 100px 80px" }}>
            {["Assignment", "Course", "Marks", "Due Date", "Submissions"].map(h => (
              <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
            ))}
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
            : assignments.map((a, i) => (
            <div key={a._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "2fr 1fr 80px 100px 80px" }}>
              <div>
                <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{a.title}</p>
                <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] line-clamp-1">{a.description}</p>
              </div>
              <span className="font-['Syne'] font-bold text-[10px] text-[#0A7AFF]">{a.course_code}</span>
              <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">{a.max_marks}</span>
              <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{a.due_date}</span>
              <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{a.submissions || 0}</span>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">New Assignment</h3>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <form onSubmit={handleAddAssignment} className="space-y-5">
                {[["title", "Title"], ["course_code", "Course Code"], ["course_name", "Course Name"], ["description", "Description"], ["due_date", "Due Date"]].map(([f, l]) => (
                  <div key={f}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">{l}</label>
                    <input type={f === "due_date" ? "date" : "text"} value={form[f]} onChange={e => setForm(p => ({...p, [f]: e.target.value}))} required
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                  </div>
                ))}
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors">Create</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
